// =============================================================================
// domain/accountDeletion.ts — scheduling and cancelling an account deletion.
// -----------------------------------------------------------------------------
// ── 🚨 DELETION IS A SCHEDULE, NOT AN ACT ──
//
// "Delete my account" inserts ONE ROW into `account_deletions`. Nothing is
// erased. Every trade, journal entry, screenshot and setting stays exactly where
// it is, and signing back in and pressing Restore removes the row and the whole
// thing never happened.
//
// A cron job hard-deletes rows older than the grace window. THAT is the
// irreversible step, and it happens 30 days later without anyone pressing
// anything.
//
// ── 🚨 THE DATE ON SCREEN IS A PROMISE ABOUT WHEN SOMEONE'S DATA DIES ──
//
// So it is computed from the STORED `requested_at`, never from the browser's
// clock at the moment the screen is drawn. Those differ by however long the user
// has been signed in, and "you have until the 2nd" being off by a day is the
// kind of wrong that only reveals itself after the data is gone.
// =============================================================================

/**
 * ⚠ MUST MATCH `DELETION_GRACE_DAYS` IN THE PURGE FUNCTION'S ENVIRONMENT.
 *
 * The server reads it from an env var defaulting to 30; this is a hardcoded 30
 * on both clients. If that variable is ever changed, this constant has to change
 * with it — otherwise the app shows a date that is not when the purge runs, and
 * the user plans around a deadline that does not exist.
 */
export const DELETION_GRACE_DAYS = 30;

const DAY_MS = 86_400_000;

/**
 * When the data is actually erased, from the moment the deletion was requested.
 *
 * ⚠ Takes `requestedAt`, not "now". A user who scheduled deletion last Tuesday
 * and opens the app today must see LAST TUESDAY plus thirty days — computing
 * from the current clock would quietly extend the deadline every time they
 * looked at it.
 *
 * Returns null for an unreadable stamp rather than a wrong date: no date at all
 * is honest, and "Invalid Date" beside "permanently erased" is alarming and
 * useless at the same time.
 */
export function purgeDate(requestedAt: string | null | undefined): Date | null {
  if (!requestedAt) return null;
  const t = new Date(requestedAt).getTime();
  if (Number.isNaN(t)) return null;
  return new Date(t + DELETION_GRACE_DAYS * DAY_MS);
}

/** The long form used in every sentence about the deadline. */
export function formatPurgeDate(
  requestedAt: string | null | undefined, locale?: string,
): string | null {
  const d = purgeDate(requestedAt);
  return d ? d.toLocaleDateString(locale, { dateStyle: 'long' }) : null;
}

/**
 * Days left, rounded DOWN, never below zero.
 *
 * ⚠ FLOOR, not round. With 1.6 days left, "2 days" is a day of grace the user
 * does not have. Rounding down is the only direction that cannot cost them
 * anything.
 */
export function daysUntilPurge(
  requestedAt: string | null | undefined, now: Date,
): number | null {
  const d = purgeDate(requestedAt);
  if (!d) return null;
  return Math.max(0, Math.floor((d.getTime() - now.getTime()) / DAY_MS));
}

/**
 * The word the user has to type.
 *
 * ⚠ CASE-SENSITIVE and exact, matching the old client. "delete" does not pass —
 * the whole point of the box is that it cannot be satisfied by muscle memory,
 * and accepting near-misses makes it a speed bump rather than a stop.
 */
export const DELETE_CONFIRM_WORD = 'DELETE';

export const confirmWordMatches = (typed: string): boolean =>
  String(typed ?? '').trim() === DELETE_CONFIRM_WORD;

/**
 * `null` when the deletion may be attempted, otherwise the message to show.
 *
 * ⚠ The password check is SECOND. Someone who has typed the wrong confirmation
 * word has not committed to anything yet, and asking them to fix their password
 * first implies the deletion is further along than it is.
 */
export function validateDeleteRequest(
  typedWord: string, password: string,
): string | null {
  if (!confirmWordMatches(typedWord)) {
    return `Type ${DELETE_CONFIRM_WORD} exactly to confirm.`;
  }
  if (!password) return 'Your password is required.';
  return null;
}
